import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Button from '../components/UI/Button';
import ResponsiveTable from '../components/ResponsiveTable';
import ClientLink from '../components/ClientLink';
import { useSearchParams } from 'react-router-dom';
import { apiUrl } from '../config/api';
import PageTabs from '../components/UI/PageTabs';
import SearchSelect from '../components/UI/SearchSelect';
import SentMessages, { MESSAGE_SCENARIOS as SCENARIOS } from '../components/SentMessages';
import { Paperclip, X } from 'lucide-react';
import { formatBytes } from '../utils/formatBytes';

// The editor is large; it loads when the Send tab is first shown.
const RichTextEditor = React.lazy(() => import('../components/UI/RichTextEditor'));

// Attachments: the kinds and limits the API accepts.
const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_BYTES = 15 * 1024 * 1024;
const ATTACHMENT_NAME = /\.(pdf|png|jpe?g|gif|webp|docx?|xlsx?|csv|txt)$/i;
const ATTACHMENT_ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt';

// Messages: Send (write to clients) and Sent (every message sent from here,
// kept by the API). ?tab=sent opens the Sent tab.
const SendMessages = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'sent' ? 'sent' : 'send';
  const setTab = (key) => setSearchParams(key === 'sent' ? { tab: 'sent' } : {}, { replace: true });
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [statuses, setStatuses] = useState([]);
  const [clients, setClients] = useState([]);

  const [scenario, setScenario] = useState('all_clients');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState([]);

  const [subject, setSubject] = useState('');
  // The message as the editor gives it: formatted (sent) and plain (checked).
  const [messageHtml, setMessageHtml] = useState('');
  const [messageText, setMessageText] = useState('');
  const [files, setFiles] = useState([]);

  // Files picked: kept if they are of a kind that can be sent and fit the
  // limits (the API checks the same).
  const addFiles = (list) => {
    const picked = Array.from(list || []);
    const wrong = picked.filter((f) => !ATTACHMENT_NAME.test(f.name));
    const big = picked.filter((f) => ATTACHMENT_NAME.test(f.name) && f.size > MAX_FILE_BYTES);
    const ok = picked.filter((f) => ATTACHMENT_NAME.test(f.name) && f.size <= MAX_FILE_BYTES);
    const next = [...files, ...ok].slice(0, MAX_FILES);
    const problems = [];
    if (wrong.length) problems.push(`${wrong.map((f) => f.name).join(', ')}: only PDF, image, Word, Excel, CSV or text files can be attached.`);
    if (big.length) problems.push(`${big.map((f) => f.name).join(', ')}: each file can be at most 10 MB.`);
    if (files.length + ok.length > MAX_FILES) problems.push(`At most ${MAX_FILES} files.`);
    if (next.reduce((n, f) => n + f.size, 0) > MAX_TOTAL_BYTES) {
      problems.push('The files can be at most 15 MB together.');
      return setError(problems.join(' '));
    }
    setFiles(next);
    setError(problems.join(' '));
  };

  const [preview, setPreview] = useState([]);
  const [notice, setNotice] = useState('');
  // The last notice is for a message just sent (it links to Sent).
  const [sentNow, setSentNow] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      setError('');
      try {
        const res = await axios.get(apiUrl('/api/client-messages/options'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStatuses(Array.isArray(res.data?.statuses) ? res.data.statuses : []);
        setClients(Array.isArray(res.data?.clients) ? res.data.clients : []);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load message options');
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [token]);

  useEffect(() => {
    setPreview([]);
    setNotice('');
    setSentNow(false);
    setError('');
    if (scenario !== 'by_loan_status') setSelectedStatuses([]);
    if (scenario !== 'individual_client') setSelectedClientId('');
    if (scenario !== 'custom_client_list') setSelectedClientIds([]);
  }, [scenario]);

  // The clients to pick from, found by name, client code or email.
  const clientOptions = useMemo(() => clients.map((c) => ({
    value: c.id,
    label: `${c.full_name} (${c.client_code || `#${c.id}`})`,
    detail: c.email,
  })), [clients]);

  const scenarioHelp = useMemo(() => {
    if (scenario === 'all_clients') return 'Sends to all active clients with an email address.';
    if (scenario === 'by_loan_status') return 'Sends to clients whose loans match any selected status.';
    if (scenario === 'individual_client') return 'Sends to one selected client.';
    if (scenario === 'clients_without_loans') return 'Sends to active clients that currently have no loans.';
    if (scenario === 'clients_with_active_loans') return 'Sends to clients with at least one non-terminal loan.';
    if (scenario === 'custom_client_list') return 'Sends to a manual list of selected clients.';
    return '';
  }, [scenario]);

  const buildPayload = () => ({
    scenario,
    loan_statuses: selectedStatuses,
    client_id: selectedClientId ? Number(selectedClientId) : null,
    client_ids: selectedClientIds,
  });

  const validateBeforeAction = () => {
    if (scenario === 'by_loan_status' && selectedStatuses.length === 0) {
      setError('Please select at least one loan status.');
      return false;
    }
    if (scenario === 'individual_client' && !selectedClientId) {
      setError('Please select a client.');
      return false;
    }
    if (scenario === 'custom_client_list' && selectedClientIds.length === 0) {
      setError('Please select at least one client.');
      return false;
    }
    return true;
  };

  const previewRecipients = async () => {
    setError('');
    setNotice('');
    setSentNow(false);
    if (!validateBeforeAction()) return;

    setPreviewLoading(true);
    try {
      const res = await axios.post(apiUrl('/api/client-messages/preview'), buildPayload(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(res.data?.recipients) ? res.data.recipients : [];
      setPreview(rows);
      setNotice(`Preview loaded: ${res.data?.count || rows.length} recipient(s).`);
    } catch (err) {
      setPreview([]);
      setError(err.response?.data?.error || 'Failed to preview recipients.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendMessages = async () => {
    setError('');
    setNotice('');
    setSentNow(false);

    if (!validateBeforeAction()) return;
    if (!subject.trim()) {
      setError('Subject is required.');
      return;
    }
    if (!messageText) {
      setError('Message is required.');
      return;
    }

    setSending(true);
    try {
      // The fields as JSON in "payload", the files beside them.
      const form = new FormData();
      form.append('payload', JSON.stringify({
        ...buildPayload(),
        subject: subject.trim(),
        message: messageText,
        message_html: messageHtml,
      }));
      files.forEach((f) => form.append('attachments', f));
      const res = await axios.post(apiUrl('/api/client-messages/send'), form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const sent = Number(res.data?.sent || 0);
      const failed = Number(res.data?.failed_count || 0);
      setNotice(`Messages sent. Success: ${sent}${failed ? `, Failed: ${failed}` : ''}.`);
      setSentNow(true);
      if (failed > 0) {
        setError('Some messages failed. The Sent tab shows which ones and why.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send messages.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-full min-h-screen">
      <PageTabs
        tabs={[{ key: 'send', label: 'Send' }, { key: 'sent', label: 'Sent' }]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'sent' ? <SentMessages /> : (
      <div className="bg-white dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-xl p-4 sm:p-5 shadow-sm space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">
              Scenario
            </label>
            <select
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              className="w-full"
            >
              {SCENARIOS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{scenarioHelp}</p>
          </div>

          {scenario === 'by_loan_status' && (
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">
                Loan Statuses
              </label>
              <select
                multiple
                value={selectedStatuses}
                onChange={(e) => {
                  const vals = Array.from(e.target.selectedOptions).map((o) => o.value);
                  setSelectedStatuses(vals);
                }}
                className={`w-full h-[130px] ${selectedStatuses.length > 0 ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
              >
                {statuses.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          )}

          {scenario === 'individual_client' && (
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1" htmlFor="message-client">
                Client
              </label>
              <SearchSelect
                id="message-client"
                options={clientOptions}
                value={selectedClientId}
                onChange={(v) => setSelectedClientId(v === '' ? '' : String(v))}
                placeholder="Type a name, client code or email"
                invalid={!selectedClientId}
              />
            </div>
          )}

          {scenario === 'custom_client_list' && (
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1" htmlFor="message-clients">
                Clients
              </label>
              <SearchSelect
                id="message-clients"
                multiple
                options={clientOptions}
                value={selectedClientIds}
                onChange={setSelectedClientIds}
                placeholder="Type a name, client code or email to add a client"
                invalid={selectedClientIds.length === 0}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
              className={`w-full ${subject.trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Message</label>
            <React.Suspense fallback={<div className="h-[222px] rounded border border-gray-300 dark:border-slate-500 bg-gray-50 dark:bg-slate-800 animate-pulse" />}>
              <RichTextEditor
                value={messageHtml}
                onChange={({ html, text }) => { setMessageHtml(html); setMessageText(text); }}
                placeholder="Type the message to send to selected clients"
                invalid={!messageText}
              />
            </React.Suspense>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1" htmlFor="message-attachments">Attachments</label>
            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="message-attachments"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border text-sm font-medium cursor-pointer border-gray-300 dark:border-slate-500 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 ${files.length >= MAX_FILES ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <Paperclip size={15} /> Add files
              </label>
              <input
                id="message-attachments"
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
              <span className="text-xs text-slate-500 dark:text-slate-400">
                PDF, image, Word, Excel, CSV or text · up to {MAX_FILES} files, 10 MB each, 15 MB together
              </span>
            </div>
            {files.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-2">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-xs bg-blue-50 text-blue-800 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800/60">
                    <Paperclip size={12} />
                    <span className="max-w-[16rem] truncate" title={f.name}>{f.name}</span>
                    <span className="text-blue-600/70 dark:text-blue-300/70">{formatBytes(f.size)}</span>
                    <button type="button" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      className="p-0.5 rounded-full hover:bg-blue-100 dark:hover:bg-blue-800/50" aria-label={`Remove ${f.name}`} title="Remove">
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={previewRecipients} busyLabel="Loading Preview..." disabled={loadingOptions || previewLoading || sending}>
            {previewLoading ? 'Loading Preview...' : 'Preview Recipients'}
          </Button>
          <Button variant="success" onClick={sendMessages} busyLabel="Sending..." disabled={loadingOptions || sending || previewLoading}>
            {sending ? 'Sending...' : 'Send Message'}
          </Button>
        </div>

        {notice && (
          <div className="rounded-lg p-2.5 text-sm bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
            {notice}
            {sentNow && (
              <button type="button" onClick={() => setTab('sent')} className="ml-2 font-semibold underline">See it under Sent</button>
            )}
          </div>
        )}
        {error && <div className="rounded-lg p-2.5 text-sm bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">{error}</div>}

        <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 dark:bg-slate-700/30 text-sm font-semibold text-slate-700 dark:text-slate-200">
            Recipient Preview ({preview.length})
          </div>
          <div>
            <ResponsiveTable
              data={preview}
              columns={[
                {
                  field: 'full_name',
                  header: 'Client',
                  formatter: (value, row) => <><ClientLink clientId={row.client_id} name={value} /> ({row.client_code || `#${row.client_id}`})</>,
                },
                { field: 'email', header: 'Email' },
                {
                  field: 'latest_loan_status',
                  header: 'Latest Loan Status',
                  formatter: (value) => value || 'No loan',
                },
              ]}
              emptyMessage="No preview yet."
              isLoading={previewLoading}
            />
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default SendMessages;
